import {
  clear,
  createStore,
  del,
  delMany,
  entries as getEntries,
  get,
  getMany,
  keys as getKeys,
  set,
  setMany,
  update,
  values as getValues,
} from 'idb-keyval';

class IdbStore {
  public store: ReturnType<typeof createStore>;

  private name: string;

  constructor(name: string) {
    this.name = name;
    this.store = createStore(name, 'store');
  }

  private rebuildStore() {
    this.store = createStore(this.name, 'store');
  }

  private async withRetry<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (err: any) {
      if (err?.message?.includes('database connection is closing')
        || err?.name === 'InvalidStateError') {
        this.rebuildStore();
        return fn();
      }
      throw err;
    }
  }

  public set(key: string, value: any) {
    return this.withRetry(() => set(key, value, this.store));
  }

  public setMany(entries: [string, any][]) {
    return this.withRetry(() => setMany(entries, this.store));
  }

  public get<T = unknown>(key: string) {
    return this.withRetry(() => get<T>(key, this.store));
  }

  public getMany<T = unknown>(keys: string[]) {
    return this.withRetry(() => getMany<T>(keys, this.store));
  }

  public clear() {
    return this.withRetry(() => clear(this.store));
  }

  public del(key: string) {
    return this.withRetry(() => del(key, this.store));
  }

  public delMany(keys: string[]) {
    return this.withRetry(() => delMany(keys, this.store));
  }

  public entries() {
    return this.withRetry(() => getEntries(this.store));
  }

  public keys() {
    return this.withRetry(() => getKeys(this.store));
  }

  public values<T = unknown>() {
    return this.withRetry(() => getValues<T>(this.store));
  }

  public update<T = unknown>(key: string, updater: (oldValue: T | undefined) => T) {
    return this.withRetry(() => update(key, updater, this.store));
  }
}

export const MAIN_IDB_STORE = new IdbStore('tt-data');
export const PASSCODE_IDB_STORE = new IdbStore('tt-passcode');
